import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import PropTypes, { object, oneOf } from 'prop-types';
import { compose } from 'redux';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { Form as FinalForm } from 'react-final-form';
import classNames from 'classnames';
import * as validators from '../../util/validators';
import {
  Form,
  PrimaryButton,
  FieldTextInput,
  NamedRedirect,
  FieldRadioButtonGroup,
  FieldCustomSelect,
  FieldCheckbox,
  InlineTextButton,
  FieldPhoneNumberInputWithDialCode,
  SecondaryButton,
} from '../../components';
import css from './PermitAndCertificateForm.module.css';
import {
  employmentType,
  languageSelectOptions,
  listingTypes,
  radioButtonType,
  workingStatus as workStatus,
} from '../../marketplace-custom-config';
import { isEmpty } from 'lodash';

const nativeLangOptions = Object.keys(languageSelectOptions);
const textfield = 'textfield';
const errorTextField = 'errorTextField';
const maxDescLength = 200;
const minDescLength = 50;

const FormComponent = props => {
  const {
    formName,
    formId,
    permitAndCertificateNameLabel,
    permitAndCertificateNamePlaceholder,
    permitAndCertificateNameValidator,
    permitAndCertificateDescLabel,
    permitAndCertificateDescPlaceholder,
    permitAndCertificateDescValidator,
  } = props;
  return (
    <React.Fragment>
      <div>
        <FieldTextInput
          className={textfield}
          errorClassName={errorTextField}
          type="text"
          id={formId ? `${formId}.permitAndCertificateName` : 'permitAndCertificateName'}
          name={formName ? `${formName}.permitAndCertificateName` : 'permitAndCertificateName'}
          label={permitAndCertificateNameLabel}
          placeholder={permitAndCertificateNamePlaceholder}
          validate={permitAndCertificateNameValidator}
        />
        <FieldTextInput
          className={textfield}
          errorClassName={errorTextField}
          type="textarea"
          id={formId ? `${formId}.permitAndCertificateDesc` : 'permitAndCertificateDesc'}
          name={formName ? `${formName}.permitAndCertificateDesc` : 'permitAndCertificateDesc'}
          label={permitAndCertificateDescLabel}
          placeholder={permitAndCertificateDescPlaceholder}
          validate={permitAndCertificateDescValidator}
          forPermit={true}
        />
      </div>
    </React.Fragment>
  );
};

const PermitAndCertificateFormComponent = props => {
  const {
    onSubmit,
    useForm,
    intl,
    userNativeLang,
    formId,
    name: formName,
    values = {},
    formError,
    onCancel,
    ...restProps
  } = props;
  const formApi = useRef(null);
  const handleSubmit = values => {
    onSubmit(values);
  };
  const submitDisabled =
    !values?.permitAndCertificateName ||
    !values?.permitAndCertificateDesc ||
    values?.permitAndCertificateDesc?.length <= 50 ||
    !isEmpty(formError);

  const handleKeyDown = event => {
    const isEnterKey = event.keyCode === 13 || event.key.toLowerCase() === 'enter';
    if (isEnterKey) {
      event.preventDefault();
    }
  };

  const handleSave = event => {
    event.preventDefault();
    event.stopPropagation();
    if (submitDisabled) return;
    handleSubmit({ ...values, isEditMode: false });
  };

  const handleCancel = event => {
    event.preventDefault();
    event.stopPropagation();
    if (typeof onCancel === 'function') onCancel(event);
  };

  //ceritificateName name
  const permitAndCertificateNameLabel = intl.formatMessage({
    id: 'PermitAndCertificateForm.permitAndCertificateNameLabel',
  });
  const permitAndCertificateNamePlaceholder = intl.formatMessage({
    id: 'PermitAndCertificateForm.permitAndCertificateNamePlaceholder',
  });
  const permitAndCertificateNameRequriedMessage = intl.formatMessage({
    id: 'PermitAndCertificateForm.permitAndCertificateNameRequired',
  });
  const permitAndCertificateNameValidator = validators.required(
    permitAndCertificateNameRequriedMessage
  );

  //certificate desc
  const permitAndCertificateDescLabel = intl.formatMessage({
    id: 'PermitAndCertificateForm.permitAndCertificateDescLabel',
  });
  const permitAndCertificateDescPlaceholder = intl.formatMessage({
    id: 'PermitAndCertificateForm.permitAndCertificateDescPlaceholder',
  });
  const permitAndCertificateDescRequriedMessage = intl.formatMessage({
    id: 'PermitAndCertificateForm.permitAndCertificateDescRequired',
  });

  const permitAndCertificateDescMinLengthMessage = intl.formatMessage(
    { id: 'PermitAndCertificateForm.permitAndCertificateDescMinLength' },
    { minLength: minDescLength }
  );
  const permitAndCertificateDescMaxLengthMessage = intl.formatMessage(
    { id: 'PermitAndCertificateForm.permitAndCertificateDescMaxLength' },
    { maxLength: maxDescLength }
  );

  const permitAndCertificateDescValidator = validators.composeValidators(
    validators.required(permitAndCertificateDescRequriedMessage),
    validators.minLength(permitAndCertificateDescMinLengthMessage, minDescLength),
    validators.maxLength(permitAndCertificateDescMaxLengthMessage, maxDescLength)
  );

  const formComponent = (
    <FormComponent
      formId={formId}
      formName={formName}
      permitAndCertificateNameLabel={permitAndCertificateNameLabel}
      permitAndCertificateNamePlaceholder={permitAndCertificateNamePlaceholder}
      permitAndCertificateNameValidator={permitAndCertificateNameValidator}
      permitAndCertificateDescLabel={permitAndCertificateDescLabel}
      permitAndCertificateDescPlaceholder={permitAndCertificateDescPlaceholder}
      permitAndCertificateDescValidator={permitAndCertificateDescValidator}
    />
  );

  return useForm ? (
    <FinalForm
      {...restProps}
      onSubmit={handleSubmit}
      intl={intl}
      userNativeLang={userNativeLang}
      formId={formId}
      formName={formName}
      onCancel={onCancel}
      render={formRenderProps => {
        const {
          rootClassName,
          className,
          inProgress,
          invalid,
          form,
          intl,
          onCancel,
          handleSubmit,
        } = formRenderProps;
        formApi.current = form;

        const submitDisabled = invalid || inProgress;

        const clasess = classNames(css.root || rootClassName, className);

        const handleCancel = e => {
          e.preventDefault();
          e.stopPropagation();
          form.restart();
          if (typeof onCancel === 'function') onCancel(e);
        };

        return (
          <Form className={clasess} onSubmit={handleSubmit}>
            <div>{formComponent}</div>
            <div className={css.btncomp}>
              <PrimaryButton type="submit" disabled={submitDisabled} className={css.submitButton}>
                {intl.formatMessage({ id: 'PermitAndCertificateForm.saveRefBtn' })}
              </PrimaryButton>
              <InlineTextButton type="button" onClick={handleCancel}>
                {intl.formatMessage({ id: 'PermitAndCertificateForm.cancelRefBtn' })}
              </InlineTextButton>
            </div>
          </Form>
        );
      }}
    />
  ) : (
    <div onKeyDown={handleKeyDown}>
      <div>{formComponent}</div>

      <div className={css.btncomp}>
        <SecondaryButton
          type="submit"
          disabled={submitDisabled}
          onClick={handleSave}
          className={css.submitButton}
        >
          {intl.formatMessage({ id: 'PermitAndCertificateForm.saveCertBtn' })}
        </SecondaryButton>
        <InlineTextButton type="button" onClick={handleCancel}>
          {intl.formatMessage({ id: 'PermitAndCertificateForm.cancelCertBtn' })}
        </InlineTextButton>
      </div>
    </div>
  );
};

PermitAndCertificateFormComponent.defaultProps = {
  inProgress: false,
  userNativeLang: languageSelectOptions.en.value,
  useForm: false,
  values: {},
  formError: {},
};

const { bool, func } = PropTypes;

PermitAndCertificateFormComponent.propTypes = {
  inProgress: bool,

  // from injectIntl
  intl: intlShape.isRequired,
  userNativeLang: oneOf(nativeLangOptions).isRequired,
  onCancel: func.isRequired,
  useForm: bool.isRequired,
  values: object.isRequired,
  formError: object.isRequired,
};

const PermitAndCertificateForm = compose(injectIntl)(PermitAndCertificateFormComponent);
PermitAndCertificateForm.displayName = 'PermitAndCertificateForm';

export default PermitAndCertificateForm;
